// Permission constants based on actual backend permissions
export const PERMISSIONS = {
  // User permissions
  USER_CREATE: 'user.create',
  USER_READ: 'user.read',
  USER_UPDATE: 'user.update',
  USER_DELETE: 'user.delete',
  USER_ASSIGN_ROLE: 'user.assign_role',
  
  // Role permissions
  ROLE_CREATE: 'role.create',
  ROLE_READ: 'role.read',
  ROLE_UPDATE: 'role.update',
  ROLE_DELETE: 'role.delete',
  ROLE_ASSIGN_PERMISSIONS: 'role.assign_permissions',
  
  // Permission permissions
  PERMISSION_CREATE: 'permission.create',
  PERMISSION_READ: 'permission.read',
  PERMISSION_UPDATE: 'permission.update',
  PERMISSION_DELETE: 'permission.delete',
  PERMISSION_ASSIGN_ROLES: 'permission.assign_roles',
  
  // Product permissions
  PRODUCT_CREATE: 'product.create',
  PRODUCT_UPDATE: 'product.update',
  PRODUCT_DELETE: 'product.delete',
  // Availability page: mark products sold out / back in stock (publishProductAvailability).
  // Owner 2026-09-12: ShiftLead and up — the grant itself is the API's (see
  // tasks/product-availability-decisions-api-handover.md).
  PRODUCT_AVAILABILITY: 'product.availability',

  // HubRise / external platform permissions
  HUBRISE_VIEW: 'hubrise.view',
  HUBRISE_MANAGE: 'hubrise.manage',

  // Delivery settings permissions (first-party delivery zones + global switch)
  DELIVERY_VIEW: 'delivery.view',
  DELIVERY_MANAGE: 'delivery.manage',
  // Driver permission — the DeliveryDriver role holds ONLY this. Gates the /driver
  // screen and the whole driver API surface (driverDeliveries + claim/deliver/release).
  DELIVERY_DRIVE: 'delivery.drive',
  // Plan delivery routes: create, assign a driver, set stops (FrontOfHouse, Expeditor,
  // ShiftLead, Owner, SystemAdmin). Gates the routes panel on the delivery board.
  DELIVERY_PLAN: 'delivery.plan',

  // Category permissions
  CATEGORY_CREATE: 'category.create',
  CATEGORY_READ: 'category.read',
  CATEGORY_UPDATE: 'category.update',
  CATEGORY_DELETE: 'category.delete',
  
  // Order permissions - List/View
  ORDER_LIST_TODAY: 'order.list.today',
  ORDER_LIST_TODAY_PLANNED: 'order.list.today.planned',
  ORDER_LIST_7DAYS: 'order.list.7days',
  ORDER_LIST_7DAYS_PLANNED: 'order.list.7days.planned',
  ORDER_LIST_ALL: 'order.list.all',
  ORDER_DETAILS_TODAY: 'order.details.today',
  ORDER_DETAILS_7DAYS: 'order.details.7days',
  ORDER_DETAILS_ALL: 'order.details.all',
  
  // Order permissions - Actions
  ORDER_CHECKOUT: 'order.checkout',
  ORDER_STATUS_CANCEL_PAID_TODAY: 'order.status.cancel_paid_today',
  ORDER_MANAGE: 'order.manage',
  
  // Expeditor/Kitchen permissions
  EXPEDITOR_ORDER_PREPARE_START: 'expeditor.order.prepare_start',
  EXPEDITOR_ORDER_ITEM_COMPLETE: 'expeditor.order.item_complete',
  EXPEDITOR_ORDER_MARK_READY: 'expeditor.order.mark_ready',
  EXPEDITOR_DISPLAY_ACCESS: 'expeditor.display.access',
  
  // Payment permissions - Cash
  PAYMENT_CASH_CREATE: 'payment.cash.create',
  
  // Payment permissions - Terminal
  PAYMENT_TERMINAL_SELECT: 'payment.terminal.select',
  PAYMENT_TERMINAL_READ_INFO: 'payment.terminal.read_info',
  PAYMENT_TERMINAL_CANCEL: 'payment.terminal.cancel',
  
  // Refund permissions
  REFUND_UPTO_100: 'refund.upto_100',
  REFUND_ABOVE_100: 'refund.above_100',
  REFUND_SAME_DAY: 'refund.same_day',
  REFUND_UPTO_7DAYS: 'refund.upto_7days',
  // Record a refund made OUTSIDE our system (Mollie dashboard, Uber Eats / Thuisbezorgd) so the
  // books match — no money moves. Owner + SystemAdmin, no amount/date tier (API 2026-10-09).
  REFUND_RECORD_EXTERNAL: 'refund.record_external',

  // Reports permissions
  REPORTS_VIEW: 'reports.view',

  // Costing permissions
  COSTING_VIEW: 'costing.view',
  COSTING_MANAGE: 'costing.manage',
  COSTING_MARGIN_VIEW: 'costing.margin.view',
  ORDER_FINANCIAL_VIEW: 'order.financial.view',

  // Discount permissions
  DISCOUNT_VIEW: 'discount.view',
  DISCOUNT_MANAGE: 'discount.manage',

  // Stats permissions
  STATS_PRODUCT_SALES_VIEW: 'stats.product_sales.view',
  // Routes driven (Delivery Reports): Owner + SystemAdmin — the API's reporting gate for
  // deliveryRunHistory. Not delivery.plan: every driver holds that.
  STATS_DELIVERY_RUNS_VIEW: 'stats.delivery_runs.view',

  // Production permissions
  PRODUCTION_VIEW: 'production.view',
  PRODUCTION_LOG: 'production.log',

  // Staff meal permissions
  STAFF_MEAL_CREATE: 'staff_meal.create',
  STAFF_MEAL_REPORT: 'staff_meal.report',

  // Curry Me Club (loyalty) — programme administration: the program list,
  // the draft editor and the reward ladder. Members, reports and the counter
  // surface are later stages with their own permissions.
  LOYALTY_MANAGE: 'loyalty.manage',

  // Staff announcement permissions (baseline read/ack is auth-only, no permission)
  MESSAGE_WRITE: 'message.write',
  MESSAGE_READ_OTHERS: 'message.read.others',
  MESSAGE_READ_RECEIPTS: 'message.read.receipts',
} as const

// Route permission mapping - based on Frontend View Mapping document
export const ROUTE_PERMISSIONS = {
  '/dashboard': [PERMISSIONS.ORDER_LIST_TODAY, PERMISSIONS.ORDER_LIST_ALL, PERMISSIONS.REPORTS_VIEW],
  '/cash-registry': [PERMISSIONS.ORDER_CHECKOUT, PERMISSIONS.PAYMENT_CASH_CREATE],
  '/products': [PERMISSIONS.PRODUCT_CREATE, PERMISSIONS.PRODUCT_UPDATE, PERMISSIONS.PRODUCT_DELETE],
  '/products/new': [PERMISSIONS.PRODUCT_CREATE],
  '/products/:id': [PERMISSIONS.PRODUCT_CREATE, PERMISSIONS.PRODUCT_UPDATE, PERMISSIONS.PRODUCT_DELETE],
  '/products/:id/edit': [PERMISSIONS.PRODUCT_UPDATE],
  '/allergens': [PERMISSIONS.PRODUCT_CREATE, PERMISSIONS.PRODUCT_UPDATE, PERMISSIONS.PRODUCT_DELETE],
  '/products/:id/variants/new': [PERMISSIONS.PRODUCT_CREATE],
  '/products/:productId/variants/:variantId': [PERMISSIONS.PRODUCT_CREATE, PERMISSIONS.PRODUCT_UPDATE, PERMISSIONS.PRODUCT_DELETE],
  '/categories': [PERMISSIONS.CATEGORY_READ, PERMISSIONS.CATEGORY_CREATE, PERMISSIONS.CATEGORY_UPDATE, PERMISSIONS.CATEGORY_DELETE],
  '/categories/new': [PERMISSIONS.CATEGORY_CREATE],
  '/categories/:id': [PERMISSIONS.CATEGORY_READ, PERMISSIONS.CATEGORY_CREATE, PERMISSIONS.CATEGORY_UPDATE, PERMISSIONS.CATEGORY_DELETE],
  '/categories/:id/edit': [PERMISSIONS.CATEGORY_UPDATE],
  '/option-groups': [PERMISSIONS.PRODUCT_CREATE, PERMISSIONS.PRODUCT_UPDATE, PERMISSIONS.PRODUCT_DELETE],
  '/option-groups/new': [PERMISSIONS.PRODUCT_CREATE],
  '/option-groups/:id': [PERMISSIONS.PRODUCT_CREATE, PERMISSIONS.PRODUCT_UPDATE, PERMISSIONS.PRODUCT_DELETE],
  '/option-groups/:id/edit': [PERMISSIONS.PRODUCT_UPDATE],
  '/option-items': [PERMISSIONS.PRODUCT_CREATE, PERMISSIONS.PRODUCT_UPDATE, PERMISSIONS.PRODUCT_DELETE],
  '/option-items/new': [PERMISSIONS.PRODUCT_CREATE],
  '/option-items/:id': [PERMISSIONS.PRODUCT_CREATE, PERMISSIONS.PRODUCT_UPDATE, PERMISSIONS.PRODUCT_DELETE],
  '/option-items/:id/edit': [PERMISSIONS.PRODUCT_UPDATE],
  '/variant-option-groups': [PERMISSIONS.PRODUCT_CREATE, PERMISSIONS.PRODUCT_UPDATE, PERMISSIONS.PRODUCT_DELETE],
  '/variant-option-groups/new': [PERMISSIONS.PRODUCT_CREATE],
  '/variant-option-groups/:id/edit': [PERMISSIONS.PRODUCT_UPDATE],
  '/reports/accounting': [PERMISSIONS.ORDER_FINANCIAL_VIEW],
  '/reports/daily': [PERMISSIONS.ORDER_FINANCIAL_VIEW],
  '/reports/products': [PERMISSIONS.STATS_PRODUCT_SALES_VIEW],
  '/reports/delivery': [PERMISSIONS.STATS_DELIVERY_RUNS_VIEW],
  '/reports/time-slots': [PERMISSIONS.ORDER_FINANCIAL_VIEW],
  '/orders': [PERMISSIONS.ORDER_LIST_TODAY, PERMISSIONS.ORDER_LIST_TODAY_PLANNED, PERMISSIONS.ORDER_LIST_7DAYS, PERMISSIONS.ORDER_LIST_7DAYS_PLANNED, PERMISSIONS.ORDER_LIST_ALL, PERMISSIONS.ORDER_DETAILS_TODAY, PERMISSIONS.ORDER_DETAILS_7DAYS, PERMISSIONS.ORDER_DETAILS_ALL],
  '/orders/active': [PERMISSIONS.ORDER_LIST_TODAY, PERMISSIONS.ORDER_LIST_7DAYS, PERMISSIONS.ORDER_DETAILS_TODAY, PERMISSIONS.ORDER_DETAILS_7DAYS], // Active orders (1-7 days)
  // '/orders/today': [PERMISSIONS.ORDER_LIST_TODAY, PERMISSIONS.ORDER_DETAILS_TODAY],
  '/orders/display': [PERMISSIONS.ORDER_LIST_TODAY, PERMISSIONS.ORDER_LIST_ALL],
  '/orders/kitchen': [PERMISSIONS.EXPEDITOR_DISPLAY_ACCESS],
  // Delivery board (recentOrders) — mirrors what buildOrderListScope actually checks
  // server-side. Advancing an own-delivery order (READY → IN_DELIVERY → COMPLETED)
  // needs no permission beyond the order-details tier.
  '/orders/delivery': [PERMISSIONS.ORDER_LIST_TODAY, PERMISSIONS.ORDER_LIST_7DAYS, PERMISSIONS.ORDER_LIST_ALL],
  // Management browser (allOrders) — API flat-gates on order.list.all only
  '/orders/history': [PERMISSIONS.ORDER_LIST_ALL],
  '/users': [PERMISSIONS.USER_READ],
  '/roles': [PERMISSIONS.ROLE_READ],
  '/discounts': [PERMISSIONS.DISCOUNT_VIEW],
  '/discounts/new/:type': [PERMISSIONS.DISCOUNT_MANAGE],
  '/discounts/:id': [PERMISSIONS.DISCOUNT_VIEW],
  '/discounts/:id/edit': [PERMISSIONS.DISCOUNT_MANAGE],
  '/costing/raw-materials': [PERMISSIONS.COSTING_MANAGE],
  '/costing/raw-materials/new': [PERMISSIONS.COSTING_MANAGE],
  '/costing/raw-materials/:id': [PERMISSIONS.COSTING_MANAGE],
  '/costing/recipes': [PERMISSIONS.COSTING_MANAGE],
  '/costing/recipes/new': [PERMISSIONS.COSTING_MANAGE],
  '/costing/recipes/:id': [PERMISSIONS.COSTING_MANAGE],
  '/costing/variants': [PERMISSIONS.COSTING_MANAGE],
  '/costing/option-item-costs': [PERMISSIONS.COSTING_MANAGE],
  '/costing/bundles': [PERMISSIONS.COSTING_MANAGE],
  '/end-of-shift': [PERMISSIONS.PRODUCTION_VIEW],
  '/availability': [PERMISSIONS.PRODUCT_AVAILABILITY],
  '/external-platforms': [PERMISSIONS.HUBRISE_VIEW],
  // Announcements reader feed (/announcements) is baseline — intentionally no entry.
  // Admin flow lives under /admin/announcements.
  '/admin/announcements': [PERMISSIONS.MESSAGE_READ_OTHERS, PERMISSIONS.MESSAGE_WRITE],
  '/admin/announcements/new': [PERMISSIONS.MESSAGE_WRITE],
  '/admin/announcements/types': [PERMISSIONS.MESSAGE_WRITE],
  '/admin/announcements/:id/edit': [PERMISSIONS.MESSAGE_WRITE],
  '/admin/announcements/:id/receipts': [PERMISSIONS.MESSAGE_READ_RECEIPTS],
  '/admin/delivery': [PERMISSIONS.DELIVERY_VIEW],
  // Curry Me Club (its own sidebar group, not Admin)
  '/loyalty/programs': [PERMISSIONS.LOYALTY_MANAGE],
  '/loyalty/programs/:id': [PERMISSIONS.LOYALTY_MANAGE],
  '/loyalty/members': [PERMISSIONS.LOYALTY_MANAGE],
  // Driver screen (driverDeliveries) — its own permission, NOT the order.list tier:
  // a driver-only token hard-errors on recentOrders/order details by design.
  '/driver': [PERMISSIONS.DELIVERY_DRIVE],
} as const

// Navigation permission mapping
export const NAVIGATION_PERMISSIONS = {
  'Dashboard': [PERMISSIONS.ORDER_LIST_TODAY, PERMISSIONS.ORDER_LIST_ALL, PERMISSIONS.REPORTS_VIEW],
  'Cash Registry': [PERMISSIONS.ORDER_CHECKOUT, PERMISSIONS.PAYMENT_CASH_CREATE],
  // Reports group children. Nav gating is OR-only; the Time Slots PAGE
  // additionally requires order.list.all (adding it here would WIDEN visibility).
  'Accounting Reports': [PERMISSIONS.ORDER_FINANCIAL_VIEW],
  'Daily Reports': [PERMISSIONS.ORDER_FINANCIAL_VIEW],
  'Product Reports': [PERMISSIONS.STATS_PRODUCT_SALES_VIEW],
  'Delivery Reports': [PERMISSIONS.STATS_DELIVERY_RUNS_VIEW],
  'Time Slots': [PERMISSIONS.ORDER_FINANCIAL_VIEW],
  'Orders': [PERMISSIONS.ORDER_LIST_TODAY, PERMISSIONS.ORDER_LIST_TODAY_PLANNED, PERMISSIONS.ORDER_LIST_7DAYS, PERMISSIONS.ORDER_LIST_7DAYS_PLANNED, PERMISSIONS.ORDER_LIST_ALL, PERMISSIONS.ORDER_DETAILS_TODAY, PERMISSIONS.ORDER_DETAILS_7DAYS, PERMISSIONS.ORDER_DETAILS_ALL],
  'Active Orders': [PERMISSIONS.ORDER_LIST_TODAY, PERMISSIONS.ORDER_LIST_7DAYS, PERMISSIONS.ORDER_DETAILS_TODAY, PERMISSIONS.ORDER_DETAILS_7DAYS], // Active orders (1-7 days)
  'Delivery': [PERMISSIONS.ORDER_LIST_TODAY, PERMISSIONS.ORDER_LIST_7DAYS, PERMISSIONS.ORDER_LIST_ALL],
  // Management browser (allOrders) — API flat-gates on order.list.all only
  'Order History': [PERMISSIONS.ORDER_LIST_ALL],
  // "Today's Orders": [PERMISSIONS.ORDER_LIST_TODAY, PERMISSIONS.ORDER_DETAILS_TODAY],
  'Customer Display': [PERMISSIONS.ORDER_LIST_TODAY, PERMISSIONS.ORDER_LIST_ALL],
  'Kitchen Display': [PERMISSIONS.EXPEDITOR_DISPLAY_ACCESS],
  'Products': [PERMISSIONS.PRODUCT_CREATE, PERMISSIONS.PRODUCT_UPDATE, PERMISSIONS.PRODUCT_DELETE],
  'Allergens & Attributes': [PERMISSIONS.PRODUCT_CREATE, PERMISSIONS.PRODUCT_UPDATE, PERMISSIONS.PRODUCT_DELETE],
  'Categories': [PERMISSIONS.CATEGORY_CREATE, PERMISSIONS.CATEGORY_UPDATE],
  'Option Groups': [PERMISSIONS.PRODUCT_CREATE, PERMISSIONS.PRODUCT_UPDATE, PERMISSIONS.PRODUCT_DELETE],
  'Option Items': [PERMISSIONS.PRODUCT_CREATE, PERMISSIONS.PRODUCT_UPDATE, PERMISSIONS.PRODUCT_DELETE],
  'Variant Options': [PERMISSIONS.PRODUCT_CREATE, PERMISSIONS.PRODUCT_UPDATE, PERMISSIONS.PRODUCT_DELETE],
  'Users': [PERMISSIONS.USER_READ],
  'Roles': [PERMISSIONS.ROLE_READ],
  'Automatic': [PERMISSIONS.DISCOUNT_VIEW],
  'Coupon Codes': [PERMISSIONS.DISCOUNT_VIEW],
  'Staff Discounts': [PERMISSIONS.DISCOUNT_VIEW],
  'Raw Materials': [PERMISSIONS.COSTING_MANAGE],
  'Recipes': [PERMISSIONS.COSTING_MANAGE],
  'Variant Costs': [PERMISSIONS.COSTING_MANAGE],
  'Option Item Costs': [PERMISSIONS.COSTING_MANAGE],
  // Every COGS-group item needs an entry: canSeeNavigation treats an unmapped key
  // as visible-to-everyone, and ONE leaked child also renders the whole group
  // header. 'Bundle Costs' was missing and put COGS in every sidebar, drivers
  // included (data was still API-blocked — this was a nav leak, not a data leak).
  'Bundle Costs': [PERMISSIONS.COSTING_MANAGE],
  'End of Shift': [PERMISSIONS.PRODUCTION_VIEW],
  'Availability': [PERMISSIONS.PRODUCT_AVAILABILITY],
  'External Platforms': [PERMISSIONS.HUBRISE_VIEW],
  // Admin-group announcements management (distinct key from the baseline reader item)
  'AdminAnnouncements': [PERMISSIONS.MESSAGE_READ_OTHERS, PERMISSIONS.MESSAGE_WRITE],
  // Admin-group delivery settings (the key 'Delivery' belongs to the Delivery Board)
  'AdminDelivery': [PERMISSIONS.DELIVERY_VIEW],
  // Curry Me Club group. An UNMAPPED nav key is visible to everyone and one
  // leaked child renders the whole group header — see the 'Bundle Costs' note above.
  'ClubPrograms': [PERMISSIONS.LOYALTY_MANAGE],
  'ClubMembers': [PERMISSIONS.LOYALTY_MANAGE],
  // Driver screen (the key 'Delivery' belongs to the Delivery Board)
  'Driver': [PERMISSIONS.DELIVERY_DRIVE],
} as const
